"use client";

// useExtension — is the innernet extension in this browser, and can it coach?
//
// The extension's bridge (apps/extension/src/content/bridge.ts) runs on
// innernet's pages and answers a hello with `innernet-extension-here`: its
// version, and the tools it can coach a connector setup for (Claude, ChatGPT).
// The bridge and the page load in either order, so the page says hello a few
// times over its first seconds, and the bridge also announces itself once.
//
// `coach(tool, url)` asks it to open that tool's connectors page with a card
// that points at each control and fills the name and the link in. The
// extension refuses anything but innernet's own door; this only asks.

import { useCallback, useEffect, useState } from "react";

export interface ExtensionHere {
  version: string;
  coach: string[];
}

const HELLO_AT_MS = [0, 400, 1500];

export function parseHere(data: unknown): ExtensionHere | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.type !== "innernet-extension-here") return null;
  return {
    version: typeof d.version === "string" ? d.version.slice(0, 20) : "",
    coach: Array.isArray(d.coach) ? d.coach.filter((x): x is string => typeof x === "string").slice(0, 8) : [],
  };
}

export function useExtension(): {
  here: ExtensionHere | null;
  canCoach: (tool: string) => boolean;
  coach: (tool: string, url: string) => Promise<boolean>;
} {
  const [here, setHere] = useState<ExtensionHere | null>(null);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const got = parseHere(e.data);
      if (got) setHere(got);
    };
    window.addEventListener("message", onMessage);
    const timers = HELLO_AT_MS.map((ms) =>
      setTimeout(() => window.postMessage({ type: "innernet-extension-hello" }, window.location.origin), ms),
    );
    return () => {
      window.removeEventListener("message", onMessage);
      timers.forEach(clearTimeout);
    };
  }, []);

  const canCoach = useCallback((tool: string) => !!here?.coach.includes(tool), [here]);

  const coach = useCallback((tool: string, url: string) => new Promise<boolean>((resolve) => {
    const done = (ok: boolean) => {
      window.removeEventListener("message", onReply);
      clearTimeout(timer);
      resolve(ok);
    };
    const onReply = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; ok?: boolean };
      if (d?.type === "innernet-extension-coaching") done(!!d.ok);
    };
    const timer = setTimeout(() => done(false), 4000);
    window.addEventListener("message", onReply);
    window.postMessage({ type: "innernet-extension-coach", tool, url }, window.location.origin);
  }), []);

  return { here, canCoach, coach };
}

/** the catalogue's tool ids → the coach's own names (apps/extension/src/lib/coach) */
export const COACH_FOR_TOOL: Record<string, string> = { "claude-desktop": "claude", chatgpt: "chatgpt" };
